/**
 * Cart Cross-Sell Recommendations (1A.3)
 * Summer Moon Phase 1 — Storefront Experience PRD
 *
 * Custom element that fetches and renders product recommendations
 * in the cart drawer as a single-card carousel with prev/next arrows
 * (UpCart pattern, per Lauren's 4/30 reference). Supports three data
 * sources:
 *   1. Metafield handles (curated, via custom.cross_sell_handles)
 *   2. Shopify Recommendations API (algorithmic fallback)
 *   3. Hardcoded merch handles (for subscription carts)
 *
 * Re-initializes automatically on both Dawn section rendering paths
 * via connectedCallback (Path 1: renderContents, Path 2: onCartUpdate).
 */

const MAX_PRODUCTS = 6;
const RETRY_DELAY_MS = 1500;

// Module-scope cache for the merch collection so every cart-drawer re-render
// doesn't re-fetch the full 50-product JSON. Per-session; resets on reload.
let merchPromise = null;

class CartCrossSells extends HTMLElement {
  _loading = false;
  currentIndex = 0;
  totalCards = 0;

  connectedCallback() {
    if (this._loading) return;
    this._loading = true;
    this.loadRecommendations()
      .catch(() => this.hide())
      .finally(() => { this._loading = false; });
  }

  async loadRecommendations() {
    const track = this.querySelector('.cart-cross-sells__track');
    if (!track) return;
    track.innerHTML = this.skeletonHTML();

    const cartProductIds = (this.dataset.cartProductIds || '').split(',').filter(Boolean).map(Number);
    const metafieldHandles = this.dataset.crossSellHandles
      ? this.dataset.crossSellHandles.split(',').filter(Boolean)
      : [];

    let products = [];

    try {
      products = await this.fetchProducts(metafieldHandles);
    } catch (e) {
      console.error('[cart-cross-sells] Fetch failed, retrying...', e);
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      try {
        products = await this.fetchProducts(metafieldHandles);
      } catch (retryErr) {
        console.error('[cart-cross-sells] Retry failed', retryErr);
        this.hide();
        return;
      }
    }

    // Filter out products already in cart
    products = products.filter(p => p && !cartProductIds.includes(p.id));

    // Filter to available products with variants
    products = products.filter(p => p.available && p.variants && p.variants.length > 0);

    // Filter to products with a usable image — avoids rendering blank cards
    // when a product has images: [] or a non-string first image.
    products = products.filter(p => Array.isArray(p.images) && p.images[0]);

    if (products.length === 0) {
      this.hide();
      return;
    }

    this.renderCards(products.slice(0, MAX_PRODUCTS), track);
    this.setupCarousel();
  }

  async fetchProducts(metafieldHandles) {
    if (metafieldHandles.length > 0) {
      return this.fetchByHandles(metafieldHandles);
    }
    // Default (including subscription carts): pull live from the merchandise
    // collection so the trio auto-tracks inventory rotation. Summer Moon keeps
    // OOS products archived, so "active in /collections/merchandise" == in stock.
    return this.fetchMerchCollection();
  }

  async fetchByHandles(handles) {
    const results = await Promise.allSettled(
      handles.map(h =>
        fetch(`/products/${encodeURIComponent(h)}.json`)
          .then(r => {
            if (!r.ok) throw new Error(`${r.status}`);
            return r.json();
          })
          .then(d => d.product)
      )
    );
    return results
      .filter(r => r.status === 'fulfilled' && r.value)
      .map(r => this.normalizeProduct(r.value));
  }

  async fetchMerchCollection() {
    // Session-cached — every cart re-render was re-fetching ~200KB of JSON.
    // Known limitations (documented rather than fixed — scope-appropriate):
    //   1. No TTL. Cache resets only on full page reload or fetch error.
    //      If merch collection changes mid-session (staff add/draft a
    //      product), customers in that tab see the stale list. Acceptable
    //      given the merch collection turns over slowly.
    //   2. Cached products are returned by reference — `normalizeProduct`
    //      mutates in-place. Subsequent renders read the already-normalized
    //      objects, which is correct. Do NOT mutate `product.images` or
    //      `product.variants` in any render path (renderCards only reads).
    if (!merchPromise) {
      merchPromise = fetch('/collections/merchandise/products.json?limit=50')
        .then(resp => {
          if (!resp.ok) throw new Error(`Merch collection ${resp.status}`);
          return resp.json();
        })
        .then(data => (data.products || []).map(p => this.normalizeProduct(p)))
        .catch(err => { merchPromise = null; throw err; });
    }
    const products = await merchPromise;
    // Shuffle a COPY of the array (shallow) so each cart-open surfaces a
    // different trio. Product OBJECTS are still shared refs across renders.
    const shuffled = products.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // The Recommendations API, Collection endpoint, and single-product endpoint
  // return subtly different product shapes. Normalize to the Recommendations
  // shape that renderCards() expects: images=string[], variants[].price=cents (int),
  // top-level `available` derived from variants.
  normalizeProduct(p) {
    if (Array.isArray(p.images)) {
      p.images = p.images
        .map(img => typeof img === 'string' ? img : (img && img.src) || '')
        .filter(Boolean);
    }
    if (Array.isArray(p.variants)) {
      p.variants.forEach(v => {
        if (typeof v.price === 'string') {
          v.price = Math.round(parseFloat(v.price) * 100);
        }
      });
    }
    p.available = Array.isArray(p.variants) && p.variants.some(v => v.available);
    return p;
  }

  renderCards(products, track) {
    const moneyFormat = window.Shopify?.money_format || '${{amount}}';
    this.totalCards = products.length;

    track.innerHTML = products.map((product, i) => {
      const availableVariants = product.variants.filter(v => v.available);
      const defaultVariant = availableVariants[0] || product.variants[0];
      // Show the dropdown whenever the product has 2+ variants total — even
      // when most are sold out — so the customer can see the option set
      // (Skip 2026-05-08). Sold-out variants render `disabled` with a "(sold
      // out)" suffix. Previously we filtered to availableVariants only, which
      // made products with 4 sizes-but-3-sold-out appear to have no selector.
      const isMultiVariant = product.variants.length > 1;
      // Image URL guard: escapeHtml neutralizes HTML chars but does NOT
      // neutralize `javascript:` scheme URLs in src attributes. Curated
      // metafield handles can pull product JSON from external paths;
      // require https:// before injecting. (Clare Rev 1 finding 2026-05-08.)
      const rawImage = product.images && product.images[0]
        ? product.images[0].replace(/\.([^.]+)$/, '_240x240.$1')
        : '';
      const image = /^https?:\/\//i.test(rawImage) || rawImage.startsWith('//')
        ? rawImage
        : '';
      const price = this.formatMoney(defaultVariant.price, moneyFormat);
      const title = this.escapeHtml(product.title);
      const productUrl = `/products/${this.escapeHtml(product.handle || '')}`;
      const slideLabel = `Recommendation ${i + 1} of ${products.length}`;

      const variantSelectHtml = isMultiVariant
        ? `<select class="cart-cross-sells__variant-select" aria-label="Choose ${title} option">
            ${product.variants.map(v => {
              const selected = v.id === defaultVariant.id ? ' selected' : '';
              const disabled = !v.available ? ' disabled' : '';
              const suffix = !v.available ? ' (sold out)' : '';
              return `<option value="${v.id}" data-price="${v.price}"${selected}${disabled}>${this.escapeHtml(v.title)}${suffix}</option>`;
            }).join('')}
          </select>`
        : '';

      return `
        <div class="cart-cross-sells__card" role="listitem" aria-roledescription="slide" aria-label="${slideLabel}">
          ${image ? `
            <a href="${productUrl}" class="cart-cross-sells__card-image" tabindex="-1" aria-hidden="true">
              <img src="${this.escapeHtml(image)}" alt="" loading="${i === 0 ? 'eager' : 'lazy'}" width="120" height="120">
            </a>` : ''}
          <div class="cart-cross-sells__card-content">
            <a href="${productUrl}" class="cart-cross-sells__card-title-link">
              <p class="cart-cross-sells__card-title">${title}</p>
            </a>
            <p class="cart-cross-sells__card-price">${price}</p>
            <div class="cart-cross-sells__card-controls">
              ${variantSelectHtml}
              <button
                class="cart-cross-sells__add-btn"
                data-variant-id="${defaultVariant.id}"
                aria-label="Add ${title} to cart"
                type="button"
              >Add</button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    track.querySelectorAll('.cart-cross-sells__add-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = btn.closest('.cart-cross-sells__card');
        const select = card ? card.querySelector('.cart-cross-sells__variant-select') : null;
        const variantId = select ? select.value : btn.dataset.variantId;
        this.addToCart(variantId, btn);
      });
    });

    // Hide a card if its image fails to load (e.g. Shopify hasn't generated
    // the 240x240 variant yet). Recompute totalCards + clamp index so the
    // carousel doesn't strand the user on a removed slide.
    track.querySelectorAll('.cart-cross-sells__card-image img').forEach(img => {
      img.addEventListener('error', () => {
        img.closest('.cart-cross-sells__card')?.remove();
        this.totalCards = track.querySelectorAll('.cart-cross-sells__card').length;
        if (this.currentIndex >= this.totalCards) {
          this.currentIndex = Math.max(0, this.totalCards - 1);
        }
        this.updateCarousel();
      }, { once: true });
    });

    // Update price display when variant changes.
    // stopPropagation prevents Dawn's <cart-drawer-items> CartItems listener
    // (cart.js L25) from treating this change event as a quantity update and
    // firing /cart/change.js with line=undefined — which 422s and surfaces
    // "There was an error while updating your cart." Root-caused 2026-04-24.
    track.querySelectorAll('.cart-cross-sells__variant-select').forEach(select => {
      select.addEventListener('change', (e) => {
        e.stopPropagation();
        const card = select.closest('.cart-cross-sells__card');
        const priceEl = card ? card.querySelector('.cart-cross-sells__card-price') : null;
        const opt = select.selectedOptions[0];
        if (priceEl && opt && opt.dataset.price) {
          priceEl.textContent = this.formatMoney(Number(opt.dataset.price), moneyFormat);
        }
      });
    });
  }

  async addToCart(variantId, btn) {
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = 'Adding\u2026';

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      // SM: only treat as error for real 4xx/5xx responses. Shopify returns
      // `status` on errors (e.g. 422), but ReCharge-proxied successful adds
      // also include a `status: 200` field — we must not trip on that.
      if (parsedState.status && Number(parsedState.status) >= 400) {
        btn.classList.remove('is-loading');
        btn.textContent = 'Unavailable';
        setTimeout(() => { btn.textContent = 'Add'; btn.disabled = false; }, 2000);
        return;
      }

      // Publish cart update event — Dawn-idiomatic, notifies other subscribers
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-cross-sells',
        productVariantId: variantId,
        cartData: parsedState,
      });

      // Update cart drawer — mirrors product-form.js pattern
      // renderContents replaces #CartDrawer innerHTML with server HTML
      // Our element is destroyed; new <cart-cross-sells> from server triggers connectedCallback
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.renderContents(parsedState);
      }
    } catch (err) {
      console.error('[cart-cross-sells] Add to cart failed', err);
      btn.classList.remove('is-loading');
      btn.textContent = 'Try again';
      setTimeout(() => { btn.textContent = 'Add'; btn.disabled = false; }, 2000);
    }
  }

  setupCarousel() {
    this.currentIndex = 0;
    // Dawn's renderContents() replaces the entire <cart-cross-sells> element
    // on every cart update — connectedCallback fires on a fresh element with
    // fresh arrow buttons. Sentinels on DOM nodes don't survive that, so we
    // attach unconditionally. Each setupCarousel call binds to the current
    // element's buttons exactly once. (Clare Rev 1 finding 2026-05-08.)
    const prev = this.querySelector('.cart-cross-sells__arrow[data-direction="prev"]');
    const next = this.querySelector('.cart-cross-sells__arrow[data-direction="next"]');

    if (prev) {
      prev.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    }
    if (next) {
      next.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    }

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
    });

    this.updateCarousel();
  }

  goTo(index) {
    if (index < 0 || index >= this.totalCards) return;
    this.currentIndex = index;
    this.updateCarousel();
  }

  updateCarousel() {
    const track = this.querySelector('.cart-cross-sells__track');
    const prev = this.querySelector('.cart-cross-sells__arrow[data-direction="prev"]');
    const next = this.querySelector('.cart-cross-sells__arrow[data-direction="next"]');
    const counter = this.querySelector('.cart-cross-sells__counter');

    if (track) {
      track.style.transform = `translateX(${-100 * this.currentIndex}%)`;
    }
    if (prev) prev.disabled = this.currentIndex === 0;
    if (next) next.disabled = this.currentIndex >= this.totalCards - 1;
    if (counter && this.totalCards > 0) {
      counter.textContent = `${this.currentIndex + 1} / ${this.totalCards}`;
    }
    // Mark the active slide for assistive tech; hidden slides are aria-hidden.
    const cards = track ? track.querySelectorAll('.cart-cross-sells__card') : [];
    cards.forEach((card, i) => {
      if (i === this.currentIndex) {
        card.removeAttribute('aria-hidden');
      } else {
        card.setAttribute('aria-hidden', 'true');
      }
    });
  }

  hide() {
    this.style.display = 'none';
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals);
  }

  skeletonHTML() {
    return `
      <div class="cart-cross-sells__card cart-cross-sells__card--skeleton" aria-hidden="true">
        <div class="cart-cross-sells__skeleton-img"></div>
        <div class="cart-cross-sells__card-content">
          <div class="cart-cross-sells__skeleton-text" style="width: 70%"></div>
          <div class="cart-cross-sells__skeleton-text" style="width: 30%"></div>
          <div class="cart-cross-sells__skeleton-text" style="width: 50%; height: 2.4rem; margin-top: 0.6rem"></div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('cart-cross-sells')) {
  customElements.define('cart-cross-sells', CartCrossSells);
}
